import { useExerciseIdsSelect } from '@entities/exercise-video/model.ts';
import { useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useExercisesListSavedData } from '@features/exercises/exercise-list/helprs.ts';
import { getVideoSources } from '@shared/helpers/maps.tsx';
import { useMobile, usePlatformSettings } from '@shared/hooks';
import { usePagesRedirect } from '@shared/hooks/use-pages-redirect.ts';
import type { VideoType } from '@shared/types/common';
import type { FormContext } from '@shared/types/SharedFormsDefinitions';
import { Card, VideoUrlModal } from '@shared/ui';
import { useOneVideoSelect } from '@shared/ui/forms/video/helpers.ts';
import * as st from './styles.module.scss';
import { SelectVideoModal } from './uiComponents/select-video-modal';
import { VideoUploadButton } from './uiComponents/video-upload-button';

type PT = Partial<FormContext> & {
  isEditMode?: boolean;
  videoType?: VideoType;
  fieldLabel?: string;
  isDisabledSelectionFromLibrary?: boolean;
};

export const FormVideo = ({
  formContextFromProps,
  isEditMode,
  videoType,
  fieldLabel = 'видео',
  isDisabledSelectionFromLibrary = false,
}: PT) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isUrlModalOpen, setIsUrlModalOpen] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const { isMobile } = useMobile();
  const { has_library } = usePlatformSettings();

  const { doSetStorageData } = useExercisesListSavedData();
  const { doRedirectWithSavePageUrl } = usePagesRedirect('librarySelection');

  let formContext = useFormContext();
  formContextFromProps && (formContext = formContextFromProps);
  const { setIsSelectingTrue } = useExerciseIdsSelect();
  const {
    watch,
    setValue,
    formState: { errors },
  } = formContext;
  const url = watch('url');
  const video_type = watch('video_type');
  const file = watch('file');
  const resetSelectedVideo = () => {
    setValue('file', '');
    setValue('url', '');
    setValue('isNewVideo', true);
    setValue('isVideoDeleted', true);
  };
  const setVideoIsNotDeleted = () => setValue('isVideoDeleted', false);

  const selectSource = (videoType: VideoType) => {
    resetSelectedVideo();
    if (videoType === 'L') {
      setIsSelectingTrue();
      doRedirectWithSavePageUrl(`/programs/library/exercises`);
      doSetStorageData({ isOneVideoSelect: true });
    } else if (videoType === 'B') {
      setValue('video_type', 'B');
      fileInputRef.current?.click();
    } else {
      setValue('video_type', videoType);
      setIsUrlModalOpen(true);
    }
    setShowVideoModal(false);
  };

  const onUpload = () => {
    if (videoType) {
      selectSource(videoType);
      return;
    }
    const videoIsSelected = !!file || !!url;
    if (videoIsSelected) {
      resetSelectedVideo();
      if (fileInputRef.current) fileInputRef.current.value = '';
    } else {
      setShowVideoModal(true);
    }
  };

  const videoTitle = useMemo(() => {
    return video_type === 'B' && file && file instanceof File ? file.name : url;
  }, [video_type, file, url]);

  // console.log('videoTitle', videoTitle);
  // console.log('video_type', video_type);
  // console.log('file', file);
  // console.log('url', url);

  const videoSources = useMemo(
    () => getVideoSources(isMobile, isDisabledSelectionFromLibrary ? false : has_library),
    [isMobile, has_library, isDisabledSelectionFromLibrary]
  );

  const hasError = () => {
    const errorFields = ['file', 'url'];
    return errorFields.some((field) => errors[field]);
  };

  return (
    <fieldset>
      <h3>{fieldLabel}</h3>
      <Card
        id={'card'}
        className={st.card}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const file = e?.dataTransfer?.files[0];
          if (file && file.type.includes('video')) {
            setValue('file', e.dataTransfer.files[0]);
            setValue('video_type', 'B');
            setVideoIsNotDeleted();
          }
        }}
      >
        <VideoUploadButton
          onClick={onUpload}
          value={videoTitle}
          error={hasError()}
          uploaded={isEditMode}
        />
      </Card>

      <SelectVideoModal isOpen={showVideoModal} onClose={() => setShowVideoModal(false)}>
        {videoSources.map((item, index) => (
          <li key={index} className={st.item} onClick={() => selectSource(item.videoType)}>
            <div>
              <div className={st.item__icon}>{item.icon}</div>
              <span>{item.text}</span>
            </div>
          </li>
        ))}
      </SelectVideoModal>

      <VideoUrlModal
        videoType={videoType}
        isOpen={isUrlModalOpen}
        close={() => setIsUrlModalOpen(false)}
        saveUrl={(url) => {
          setValue('url', url, { shouldDirty: true });
          setVideoIsNotDeleted();
        }}
      />

      {formContextFromProps ? (
        <input
          className={st.hidden}
          type="file"
          accept="video/*, .mp4"
          ref={fileInputRef}
          onChange={(e) => setValue('file', e.target.files ? e.target.files[0] : null)}
        />
      ) : (
        <Controller
          name="file"
          render={({ field: { onChange } }) => (
            <input
              className={st.hidden}
              type="file"
              accept="video/*, .mp4"
              ref={fileInputRef}
              onChange={(e) => {
                onChange(e.target.files ? e.target.files[0] : null);
                setVideoIsNotDeleted();
              }}
            />
          )}
        />
      )}
    </fieldset>
  );
};
